import { toDate } from '@art-suite/art-core-ts';
import { ApiScan, getScanDataQueryKey, getScanPartHistoryQueryKey, getScanTemplatePartHistoryQueryKey, type ScanPartHistoryRecord } from '@kaiizen/kai-pro-api';
import { QueryCache, useCachedQuery, useQueryCache, useRefetchSignal } from '@kaiizen/react-persistent-cache';

import { useKaiProApiClient } from '../useKaiProApiClient';
import { useScan } from './useScans';

/**
 * `from` is inclusive, `to` is exclusive. `limit` may exceed the API's page size: the client fetches as
 * many pages as needed. Omit `limit` to fetch the full history.
 */
export type ScanPartHistoryHookOptions = { from?: Date; to?: Date; limit?: number };

const compareNewestFirst = (a: ScanPartHistoryRecord, b: ScanPartHistoryRecord) => b.updatedAt - a.updatedAt || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0);

/** Merges newly fetched records into a newest-first history: dedupes by id, keeps newest-first order, trims to `limit`. */
export const mergeScanPartHistory = (
  currentHistory: ScanPartHistoryRecord[],
  fetchedHistory: ScanPartHistoryRecord[],
  limit?: number,
): ScanPartHistoryRecord[] => {
  const knownIds = new Set(currentHistory.map(record => record.id));
  const newRecords = fetchedHistory.filter(record => !knownIds.has(record.id));
  if (newRecords.length === 0) return currentHistory;
  const mergedHistory = [...newRecords, ...currentHistory].sort(compareNewestFirst);
  return limit ? mergedHistory.slice(0, limit) : mergedHistory;
};

/**
 * Every version of a scan part and all its descendants, newest first, across the scan's direct ancestry.
 * Online only.
 *
 * Unless `to` is set, it stays current: whenever the scan's scan-data refetch signal fires (realtime
 * "scan data updated" events from other sessions, app refocus, coming back online), it fetches only the
 * records at or after the newest one it already has and merges them in.
 */
export const useScanPartHistory = (
  scanIdOrScan: string | ApiScan | undefined,
  scanPartId: string | undefined,
  options: ScanPartHistoryHookOptions = {},
) => {
  const { scan } = useScan(scanIdOrScan);
  const scanId = scan?.id;
  const { from, to, limit } = options;
  const kaiProApiClient = useKaiProApiClient();
  const queryCache = useQueryCache() as QueryCache<ScanPartHistoryRecord[]>;
  const historyQueryKey = getScanPartHistoryQueryKey(scanId, scanPartId, options);

  const query = useCachedQuery(
    historyQueryKey,
    () => kaiProApiClient.getAllScanPartHistory(scanId!, scanPartId!, options),
    { enabled: !!kaiProApiClient.token && !!scanId && !!scanPartId },
  );

  const fetchNewHistory = async () => {
    const currentHistory = (await queryCache.cache.getEntry(historyQueryKey))?.value ?? [];
    const newestUpdatedAt = currentHistory[0]?.updatedAt;
    const fetchedHistory = await kaiProApiClient.getAllScanPartHistory(scanId!, scanPartId!, {
      from: newestUpdatedAt ? toDate(Math.max(newestUpdatedAt, from?.getTime() ?? 0)) : from,
      limit,
    });
    const mergedHistory = mergeScanPartHistory(currentHistory, fetchedHistory, limit);
    if (mergedHistory !== currentHistory) await queryCache.cache.set(historyQueryKey, mergedHistory);
  };

  useRefetchSignal(
    getScanDataQueryKey(scanId, scan?.parentScanAt ? toDate(scan.parentScanAt) : undefined),
    fetchNewHistory,
    { enabled: !!kaiProApiClient.token && !!scanId && !!scanPartId && !to },
  );

  return { ...query, scanPartHistory: query.data ?? [] };
};

/** Every version of a scan template part and all its descendants (including overrides), newest first. */
export const useScanTemplatePartHistory = (
  scanTemplateId: string | undefined,
  scanPartId: string | undefined,
  options: ScanPartHistoryHookOptions = {},
) => {
  const kaiProApiClient = useKaiProApiClient();
  const query = useCachedQuery(
    getScanTemplatePartHistoryQueryKey(scanTemplateId, scanPartId, options),
    () => kaiProApiClient.getAllScanTemplatePartHistory(scanTemplateId!, scanPartId!, options),
    { enabled: !!kaiProApiClient.token && !!scanTemplateId && !!scanPartId },
  );
  return { ...query, scanPartHistory: query.data ?? [] };
};
